import { speaksPortuguese } from '../lib/copy';
// askbox-utils.js — Pure utility functions and constants extracted from AskBox.jsx

export const MOCK_AUTH_SUCCESS_CODE = '11111';
export const MOCK_AUTH_FAILURE_CODE = '00000';
export const EMAIL_SYNTAX_PATTERN =
  /^[A-Z0-9._%+-]+@[A-Z0-9](?:[A-Z0-9-]*[A-Z0-9])?(?:\.[A-Z0-9](?:[A-Z0-9-]*[A-Z0-9])?)+$/i;
export const DISALLOWED_EMAIL_DOMAINS = new Set([
  'mailinator.com',
  'mailinator.net',
  'mailinator.org',
  'yopmail.com',
  'yopmail.fr',
  'yopmail.net',
  'cool.fr.nf',
  'jetable.fr.nf',
  'nospam.ze.tc',
  'nomail.xl.cx',
  'mega.zik.dj',
  'speed.1s.fr',
  'courriel.fr.nf',
  'moncourrier.fr.nf',
  'monemail.fr.nf',
]);

export const MOCK_AUTH_STORAGE_KEY = 'concierge_mock_auth_session';

/**
 * Helper: Detect if answer came from curated answer cache
 */
export function isCuratedAnswer(value) {
  if (!value) return false;

  if (typeof value === 'object') {
    if (value.isCurated || value.answer_id) {
      return true;
    }
    const reasoning = typeof value.reasoning === 'string' ? value.reasoning : '';
    return reasoning.toLowerCase().includes('curated answer cache');
  }

  if (typeof value === 'string') {
    return value.toLowerCase().includes('curated answer cache');
  }

  return false;
}

export { speaksPortuguese };

// Painted before the server's first status event. The server's own lines live
// in concierge-api/agents/concierge/response_copy.py; the Portuguese here must
// match its "status_reviewing_request", or the bubble changes language mid-turn.
// Approved by the owner 2026-09-26.
export function getPendingStatusMessage({ isCuratedFetch, locale }) {
  if (speaksPortuguese(locale)) {
    return isCuratedFetch ? 'Carregando...' : 'Lendo sua mensagem...';
  }
  return isCuratedFetch ? 'Loading...' : 'Reviewing your request...';
}

export function getStageLabel(stage) {
  const labels = {
    init: 'Starting',
    direct: 'Direct',
    memory: 'Memory',
    retrieval: 'Retrieval',
    search: 'Search',
    research: 'Research',
    draft: 'Draft',
    generating: 'Draft',
    review: 'Review',
    finalizing: 'Finalizing',
  };
  return labels[stage] || 'Working';
}

export function getStageClasses(stage) {
  if (stage === 'memory' || stage === 'direct') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }
  if (stage === 'research') {
    return 'border-sky-200 bg-sky-50 text-sky-700';
  }
  if (stage === 'review' || stage === 'finalizing') {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }
  return 'border-indigo-200 bg-indigo-50 text-indigo-700';
}

export function getToolLabel(tool) {
  const labels = {
    search_web: 'Live Web',
    search_kb: 'Knowledge Base',
    search_curated: 'Quick Match',
    finalize: 'Synthesis',
  };
  return labels[tool] || 'Tool';
}

export function getToolClasses(tool) {
  if (tool === 'search_web') {
    return 'border-sky-200 bg-sky-50 text-sky-700';
  }
  if (tool === 'search_kb' || tool === 'search_curated') {
    return 'border-slate-200 bg-white text-slate-600';
  }
  return 'border-indigo-200 bg-indigo-50 text-indigo-700';
}

export function getLiveWebMeta(message) {
  if (!message || message.role !== 'assistant') return null;

  const usedTools = Array.isArray(message.usedTools) ? message.usedTools : [];
  const reasoning = (message.reasoning || '').toLowerCase();
  const answer = (message.content || '').toLowerCase();
  const webResearchUsed =
    Boolean(message.webResearchUsed) ||
    usedTools.includes('search_web') ||
    reasoning.includes('checked live official sources') ||
    answer.includes('(live lookup)');

  if (!webResearchUsed) {
    return null;
  }

  return {
    query: typeof message.webSearchQuery === 'string' ? message.webSearchQuery.trim() : '',
    targets: typeof message.webSearchTargets === 'string' ? message.webSearchTargets.trim() : '',
  };
}

export function getStructuredDisplay(message) {
  if (!message || typeof message !== 'object') return null;
  if (
    message.display &&
    message.display.type === 'learning_events' &&
    Array.isArray(message.display.items)
  ) {
    return message.display;
  }
  return null;
}

// A visitor watching "Lendo sua mensagem…" with nothing arriving for this long
// is told so and offered a retry, instead of a spinner that never ends (NPAS,
// 2026-10-07 18:14–18:18 São Paulo: five conversations stalled with no message).
// Longer than any normal pause between server events, shorter than the API's
// 120-second worker timeout.
export const STREAM_IDLE_TIMEOUT_MS = 60000;

// The last six turns, as the API reads them to judge a short reply ("sim").
// Our own timeout or streaming-error notice is not the assistant's turn: sent
// as one, it hid the offer the visitor's "sim" was answering
// (session-73387977c92d409f, 2026-10-11).
export function recentConversationFor(conversation) {
  return conversation
    .filter(
      (message) =>
        (message?.role === 'user' || message?.role === 'assistant') &&
        typeof message?.content === 'string' &&
        message.content.trim() &&
        !message.isStreaming &&
        !message.isSystemError,
    )
    .slice(-6)
    .map((message) => ({
      role: message.role,
      content: message.content.trim(),
      offered_live_search: Boolean(message?.offeredLiveSearch),
    }));
}

export function createIdleWatchdog(timeoutMs, onTimeout) {
  let timer = null;
  return {
    kick() {
      clearTimeout(timer);
      timer = setTimeout(onTimeout, timeoutMs);
    },
    stop() {
      clearTimeout(timer);
      timer = null;
    },
  };
}

export function readSessionCookie() {
  const allCookies = document.cookie.split(';').reduce((acc, cookie) => {
    const [name, value] = cookie.trim().split('=');
    acc[name] = value;
    return acc;
  }, {});

  if (!allCookies.session) {
    return { cookies: allCookies, session: null };
  }

  try {
    const decoded = decodeURIComponent(allCookies.session);
    return { cookies: allCookies, session: JSON.parse(decoded) };
  } catch (error) {
    console.warn('Could not parse session cookie as JSON:', error);
    return { cookies: allCookies, session: null };
  }
}

export function loadStoredMockAuth() {
  try {
    const raw = localStorage.getItem(MOCK_AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.authSessionId) return null;
    return parsed;
  } catch (error) {
    console.warn('Failed to parse stored mock auth state:', error);
    return null;
  }
}

export function persistStoredMockAuth(state) {
  localStorage.setItem(MOCK_AUTH_STORAGE_KEY, JSON.stringify(state));
}

export function clearStoredMockAuth() {
  localStorage.removeItem(MOCK_AUTH_STORAGE_KEY);
}

export function normalizeMockAuthCodeInput(value) {
  return (value || '').replace(/\D/g, '').slice(0, 5);
}

export function isValidMockAuthEmail(value) {
  const email = (value || '').trim();
  if (!email || email.length > 254 || !EMAIL_SYNTAX_PATTERN.test(email)) {
    return false;
  }
  const tld = email.slice(email.lastIndexOf('.') + 1);
  return tld.length >= 2;
}

export function isDisallowedMockAuthEmailDomain(value) {
  const email = (value || '').trim().toLowerCase();
  const atIndex = email.lastIndexOf('@');
  if (atIndex === -1) {
    return false;
  }
  const domain = email.slice(atIndex + 1);
  for (const blockedDomain of DISALLOWED_EMAIL_DOMAINS) {
    if (domain === blockedDomain || domain.endsWith(`.${blockedDomain}`)) {
      return true;
    }
  }
  return false;
}

export function buildGenericAuthPrompt() {
  return {
    type: 'mock_email_otp',
    title: 'Verify your email',
    message: 'Verify your email to unlock renewal dates, credits, and account-specific help.',
    email_label: 'Email address',
    code_label: 'Verification code',
    send_label: 'Send code',
    verify_label: 'Verify',
    help_text: `For this staged flow, ${MOCK_AUTH_SUCCESS_CODE} succeeds and ${MOCK_AUTH_FAILURE_CODE} fails.`,
  };
}

// Where a person from the team takes over the conversation, and where the
// assistant answers again. Returned per message: 'joined', 'returned' or null.
//
// Derived from the messages rather than from handover state, because the page
// hears a case close only on the visitor's next turn, and the messages already
// say who wrote what. A service notice (actor 'service') is neither: it is the
// channel speaking, and must not read as the assistant coming back.
export function handoverDividers(messages) {
  let withPerson = false;
  return messages.map((message) => {
    if (message?.role !== 'assistant') {
      return null;
    }
    if (message.fromHuman) {
      if (withPerson) {
        return null;
      }
      withPerson = true;
      return 'joined';
    }
    if (message.fromService || !withPerson) {
      return null;
    }
    withPerson = false;
    return 'returned';
  });
}

// The stored messages a completed turn carries in place of an answer, or null
// when the turn is an ordinary answer. A turn a person owns shows only these —
// even none: the visitor's message was relayed and nothing new is waiting yet.
// Treating an empty list as "no stored messages" sent that turn down the answer
// path, which drew its empty `answer` as a blank bubble under the assistant's
// name, and handoverDividers then read that bubble as the assistant returning.
export function authoredMessages(event) {
  if (event.handover_after_answer || !Array.isArray(event.messages)) {
    return null;
  }
  if (event.messages.length > 0 || event.handled_by === 'human') {
    return event.messages;
  }
  return null;
}
